import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canBill } from "../lib/roles";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Zap, Check, LogOut, Loader2, ShieldCheck, Sparkles } from "lucide-react";

const STATUS_LABEL = {
  trialing: "Trialing", active: "Active", past_due: "Past due",
  canceled: "Canceled", none: "No plan",
};

export default function Billing() {
  const { user, tenant, logout, refreshTenant } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: s }] = await Promise.all([
          api.get("/billing/plans"),
          api.get("/billing/subscription"),
        ]);
        setPlans(p);
        setSub(s);
        await refreshTenant();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshTenant]);

  const isActive = sub && ["trialing", "active"].includes(sub.status);
  const isAgency = canBill(user?.role);

  if (!loading && !isAgency) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="billing-staff-message">
        <div className="max-w-sm text-center space-y-4">
          <p className="text-lg font-display font-semibold">{isActive ? "Billing is managed by your account owner or a billing manager" : "This workspace's plan needs renewing"}</p>
          <p className="text-sm text-muted-foreground">Ask your account owner to {isActive ? "make any plan changes" : "renew the subscription"}.</p>
          {isActive ? <Button onClick={() => navigate("/dashboard")} data-testid="billing-back-button">Back to dashboard</Button>
            : <Button variant="outline" onClick={async () => { await logout(); navigate("/login"); }} data-testid="billing-logout-button">Sign out</Button>}
        </div>
      </div>
    );
  }

  const startCheckout = async (lookup) => {
    setBusy(lookup);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen text-slate-100" style={{ background: "#07080D" }} data-testid="billing-page">
      <header className="border-b" style={{ borderColor: "rgba(99,102,241,0.18)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}><Zap className="w-4 h-4 text-white" /></div>
          <span className="font-display font-extrabold text-lg">Momento</span>
          <span className="text-slate-500 text-sm ml-2 hidden sm:inline">· {tenant?.name}</span>
          <div className="ml-auto flex items-center gap-3">
            {isActive && (
              <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="billing-back-dashboard">Go to dashboard</button>
            )}
            <button onClick={async () => { await logout(); navigate("/login"); }} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="billing-logout">
              <LogOut className="w-4 h-4" />Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>
        ) : (
          <>
            {isActive ? (
              <div className="mb-10 rounded-2xl border p-6 flex flex-col sm:flex-row sm:items-center gap-4" style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.08)" }} data-testid="billing-current-status">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-emerald-300 font-semibold"><ShieldCheck className="w-5 h-5" />Subscription {STATUS_LABEL[sub.status]}</div>
                  <p className="text-slate-300 text-sm mt-1 capitalize">
                    {sub.plan ? `${sub.plan} plan` : "Active plan"}
                    {sub.status === "trialing" && sub.trial_end ? ` — trial ends ${new Date(sub.trial_end).toLocaleDateString()}` : ""}
                    {sub.status === "active" && sub.current_period_end ? ` — renews ${new Date(sub.current_period_end).toLocaleDateString()}` : ""}
                    {sub.cancel_at_period_end ? " (cancels at period end)" : ""}
                  </p>
                </div>
                <button onClick={openPortal} disabled={busy === "portal"} className="px-5 py-2.5 rounded-full font-semibold text-white inline-flex items-center gap-2" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }} data-testid="billing-manage-button">
                  {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Manage billing
                </button>
              </div>
            ) : (
              <div className="text-center max-w-2xl mx-auto mb-10">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: "rgba(139,92,246,0.35)", background: "rgba(139,92,246,0.1)", color: "#c4b5fd" }}>
                  <Sparkles className="w-3.5 h-3.5" />Start your 14-day free trial
                </span>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-5">Choose your plan</h1>
                <p className="text-slate-400 mt-3">Pick a plan to unlock your workspace. Card required — you won't be charged until your trial ends, and you can cancel anytime.</p>
              </div>
            )}

            <div className="flex items-center justify-center gap-3 mb-10">
              <span className={`text-sm ${!annual ? "text-white" : "text-slate-500"}`}>Monthly</span>
              <button onClick={() => setAnnual((a) => !a)} className="relative w-12 h-6 rounded-full transition-colors" style={{ background: annual ? "#6366F1" : "rgba(255,255,255,0.15)" }} data-testid="billing-annual-toggle">
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: annual ? "26px" : "2px" }} />
              </button>
              <span className={`text-sm ${annual ? "text-white" : "text-slate-500"}`}>Annual <span className="text-emerald-400 font-semibold">−20%</span></span>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {plans.map((p) => {
                const price = annual ? p.yearly : p.monthly;
                const lookup = annual ? p.lookup_yearly : p.lookup_monthly;
                const current = isActive && sub.plan === p.key;
                return (
                  <div key={p.key} className="relative rounded-2xl border p-6 flex flex-col" style={{ borderColor: p.popular ? "rgba(99,102,241,0.55)" : "rgba(148,163,184,0.18)", background: "rgba(14,16,23,0.7)" }} data-testid={`plan-card-${p.key}`}>
                    {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold text-white" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}>Most popular</span>}
                    <h3 className="font-display font-bold text-xl">{p.name}</h3>
                    <p className="text-slate-400 text-sm mt-1">{p.tagline}</p>
                    <div className="mt-5 flex items-end gap-1">
                      <span className="font-display font-extrabold text-4xl">${annual ? Math.round(price / 12) : price}</span>
                      <span className="text-slate-500 text-sm mb-1.5">/mo</span>
                    </div>
                    {annual && <p className="text-xs text-slate-500 mt-1">${price.toFixed(2)} billed yearly</p>}
                    <ul className="mt-5 space-y-2.5 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{f}</li>
                      ))}
                    </ul>
                    {current ? (
                      <button disabled className="mt-6 px-5 py-3 rounded-full font-semibold text-emerald-300 border border-emerald-500/40" data-testid={`plan-current-${p.key}`}>Current plan</button>
                    ) : isActive ? (
                      <button onClick={openPortal} disabled={busy === "portal"} className="mt-6 px-5 py-3 rounded-full font-semibold text-slate-200 border border-white/15 hover:bg-white/5 transition-colors" data-testid={`plan-switch-${p.key}`}>Switch in portal</button>
                    ) : (
                      <button onClick={() => startCheckout(lookup)} disabled={!!busy} className="mt-6 px-5 py-3 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]" style={{ background: p.popular ? "linear-gradient(135deg,#6366F1,#8B5CF6)" : "rgba(255,255,255,0.08)", border: p.popular ? "none" : "1px solid rgba(255,255,255,0.15)" }} data-testid={`plan-select-${p.key}`}>
                        {busy === lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Start free trial
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs text-slate-600 mt-8">Secure checkout by Stripe · Test mode — use card 4242 4242 4242 4242</p>
          </>
        )}
      </main>
    </div>
  );
}
